import React from 'react';
/** Co-located in practice/ domain (CL-1 s64). */
import { motion } from 'framer-motion';
import { Check, X, Star } from 'lucide-react';
import { DjayAvatar, DjayEmotion, useDjaySelection } from '../../../mascot';
interface DjayReactionHeaderProps {
  isCorrect: boolean;
  motivationalText: string;
  xpGained: number;
  mode?: 'full' | 'compact';
  streak?: number;
  distractorExplanation?: string;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
}
export function DjayReactionHeader({
  isCorrect,
  motivationalText,
  xpGained,
  mode = 'full',
  streak = 0,
  distractorExplanation,
  levelUp
}: DjayReactionHeaderProps) {
  if (mode === 'compact') {
    return <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-rose-200 text-rose-600'}`} data-id="element-6710">
        {isCorrect ? <Check className="w-5 h-5" data-id="element-6711" /> : <X className="w-5 h-5" data-id="element-6712" />}
      </div>;
  }
  const {
    selectedVariant
  } = useDjaySelection();
  const showDistractor = !isCorrect && !!distractorExplanation;
  const messageText = levelUp && isCorrect ? `Wauw, level ${levelUp.level}! 🎉` : showDistractor ? distractorExplanation : motivationalText;
  const djayEmotion: DjayEmotion = isCorrect ? levelUp ? 'proud' : streak >= 5 ? 'excited' : 'happy' : showDistractor ? 'thinking' : 'pointing';
  return <motion.div initial={{
    opacity: 0,
    y: -10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: 'easeOut'
  }} className={`relative flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl border-2 mb-4 ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-6713">
      <motion.div initial={{
      scale: 0.8,
      rotate: -10
    }} animate={{
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 20,
      delay: 0.1
    }} className="flex-shrink-0" data-id="element-6714">
        <DjayAvatar size="lg" emotion={djayEmotion} variant={selectedVariant} animate={true} data-id="element-6715" />
      </motion.div>

      <div className="flex-1 pr-2" data-id="element-6716">
        {showDistractor && <p className="text-[10px] font-black uppercase tracking-wider text-rose-600 mb-1" data-id="element-6717">
            Zo leer je het!
          </p>}
        <p className={`font-bold tracking-tight ${isCorrect ? 'text-emerald-800 text-lg' : showDistractor ? 'text-rose-800 text-base leading-snug' : 'text-rose-800 text-lg'} mb-1.5`} data-id="element-6718">
          {messageText}
        </p>
        {/* XP Badge */}
        {isCorrect ? <div className="inline-flex items-center gap-1 bg-emerald-100/80 px-2 py-0.5 rounded-full border border-emerald-200/60" data-id="element-6719">
            <Star className="w-3 h-3 text-emerald-600 fill-emerald-600" data-id="element-6720" />
            <span className="text-xs font-black text-emerald-700" data-id="element-6721">
              +{xpGained} XP
            </span>
          </div> : <div className="inline-flex items-center gap-1.5 bg-orange-100/80 px-2 py-0.5 rounded-full border border-orange-200/60" data-id="element-6722">
            <span className="text-xs font-black text-orange-700" data-id="element-6723">
              +{xpGained} XP
            </span>
            <span className="text-[9px] font-bold text-orange-600 uppercase tracking-wider" data-id="element-6724">
              voor de inzet
            </span>
          </div>}
      </div>

      {/* Status Icon */}
      <div className={`absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center border-2 shadow-sm ${isCorrect ? 'bg-emerald-500 border-white text-white' : 'bg-rose-500 border-white text-white'}`} data-id="element-6725">
        {isCorrect ? <Check className="w-5 h-5" strokeWidth={3} data-id="element-6726" /> : <X className="w-5 h-5" strokeWidth={3} data-id="element-6727" />}
      </div>
    </motion.div>;
}